import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shared WebSocket for Remote Media packet-level events.
 *
 * A single connection is opened lazily the first time a component subscribes
 * to a `command_id`. Multiple handlers for the same command_id are allowed.
 * Re-connects automatically after network drops.
 *
 * Usage:
 *   const { connected, subscribeToCommand } = useMediaWebSocket();
 *   useEffect(() => {
 *     if (!commandId) return;
 *     return subscribeToCommand(commandId, (event) => { ... });
 *   }, [commandId, subscribeToCommand]);
 */

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

// Module-level singleton state so multiple components share one socket.
const state = {
  socket: null,
  token: null,
  reconnectTimer: null,
  // command_id -> Set<handler>
  handlers: new Map(),
  // command_ids we have asked the server to subscribe to on the current socket.
  // Re-sent whenever the socket reopens.
  subscribedCommands: new Set(),
  connectedListeners: new Set(),
  connectedNow: false,
};

const notifyConnectedListeners = () => {
  for (const listener of state.connectedListeners) {
    try { listener(state.connectedNow); } catch { /* ignore */ }
  }
};

const sendRaw = (payload) => {
  if (state.socket && state.socket.readyState === WebSocket.OPEN) {
    state.socket.send(JSON.stringify(payload));
    return true;
  }
  return false;
};

const sendSubscribe = (commandIds) => {
  if (!commandIds.length) return;
  sendRaw({ type: 'subscribe', command_ids: commandIds });
};

const sendUnsubscribe = (commandIds) => {
  if (!commandIds.length) return;
  sendRaw({ type: 'unsubscribe', command_ids: commandIds });
};

const openSocket = () => {
  if (!state.token) return;
  if (state.socket && (state.socket.readyState === WebSocket.OPEN || state.socket.readyState === WebSocket.CONNECTING)) return;
  try {
    const ws = new WebSocket(`${WS_URL}/api/ws/media/${state.token}`);
    state.socket = ws;
    ws.onopen = () => {
      state.connectedNow = true;
      notifyConnectedListeners();
      // Re-send all pending subscriptions.
      if (state.subscribedCommands.size > 0) {
        sendSubscribe(Array.from(state.subscribedCommands));
      }
    };
    ws.onmessage = (event) => {
      let message;
      try { message = JSON.parse(event.data); } catch { return; }
      if (message.type === 'media_event' && message.command_id) {
        const set = state.handlers.get(message.command_id);
        if (set) {
          for (const handler of set) {
            try { handler(message.event, message); } catch (e) { console.error('Media WS handler error:', e); }
          }
        }
      }
      // Ignore 'connected', 'subscribed', 'pong', server pings, etc.
    };
    ws.onclose = () => {
      state.socket = null;
      state.connectedNow = false;
      notifyConnectedListeners();
      if (state.token && state.handlers.size > 0) {
        clearTimeout(state.reconnectTimer);
        state.reconnectTimer = setTimeout(openSocket, 3000);
      }
    };
    ws.onerror = () => { /* close handler will trigger reconnect */ };
  } catch (e) {
    console.error('Media WS connect error:', e);
    clearTimeout(state.reconnectTimer);
    state.reconnectTimer = setTimeout(openSocket, 3000);
  }
};

const closeSocketIfIdle = () => {
  if (state.handlers.size === 0 && state.socket) {
    try { state.socket.close(); } catch { /* ignore */ }
    state.socket = null;
  }
};

export const useMediaWebSocket = (token) => {
  const [connected, setConnected] = useState(state.connectedNow);

  useEffect(() => {
    if (!token) return undefined;
    state.token = token;
    state.connectedListeners.add(setConnected);
    openSocket();
    return () => {
      state.connectedListeners.delete(setConnected);
    };
  }, [token]);

  const subscribeToCommand = useCallback((commandId, handler) => {
    if (!commandId || typeof handler !== 'function') return () => {};
    let set = state.handlers.get(commandId);
    if (!set) {
      set = new Set();
      state.handlers.set(commandId, set);
    }
    set.add(handler);
    // Tell the server to include this command_id in our subscription list.
    if (!state.subscribedCommands.has(commandId)) {
      state.subscribedCommands.add(commandId);
      sendSubscribe([commandId]);
    }
    // Ensure the socket is actually open.
    openSocket();
    return () => {
      const bucket = state.handlers.get(commandId);
      if (!bucket) return;
      bucket.delete(handler);
      if (bucket.size === 0) {
        state.handlers.delete(commandId);
        state.subscribedCommands.delete(commandId);
        sendUnsubscribe([commandId]);
        closeSocketIfIdle();
      }
    };
  }, []);

  return { connected, subscribeToCommand };
};
